import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import { ViewIcon, AddIcon } from '../components/Icons';
import './AdminInvoices.css';

function AdminInvoices() {
  const queryClient = useQueryClient();
  const { success, error: showError, warning } = useNotification();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: '',
    order_id: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    order_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: ''
  });

  // Patikrinti ar yra create_order_id query parametras
  useEffect(() => {
    const createOrderId = searchParams.get('create_order_id');
    if (createOrderId) {
      setCreateForm(prev => ({
        ...prev,
        order_id: createOrderId
      }));
      setShowCreateModal(true);
      // Pašalinti query parametrą iš URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-invoices', filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const res = await apiClient.get(`/admin/invoices?${params.toString()}`);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/invoices', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-invoices']);
      setShowCreateModal(false);
      setCreateForm({
        order_id: '',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '',
        notes: ''
      });
      success('Sąskaita faktūra sėkmingai sukurta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant sąskaitą faktūrą');
    },
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleCreate = () => {
    if (!createForm.order_id) {
      warning('Pasirinkite užsakymą');
      return;
    }
    createMutation.mutate(createForm);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'draft': 'status-draft',
      'sent': 'status-sent',
      'paid': 'status-paid',
      'cancelled': 'status-cancelled',
    };
    return classes[status] || 'status-default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Juodraštis',
      'sent': 'Išsiųsta',
      'paid': 'Apmokėta',
      'cancelled': 'Atšaukta',
    };
    return labels[status] || status;
  };

  if (isLoading) {
    return <div className="admin-invoices-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-invoices-page container">
      <div className="invoices-header">
        <h1>Sąskaitos faktūros</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary btn-sm"
        >
          <AddIcon />
          Sukurti sąskaitą faktūrą
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">Visi</option>
              <option value="draft">Juodraštis</option>
              <option value="sent">Išsiųsta</option>
              <option value="paid">Apmokėta</option>
              <option value="cancelled">Atšaukta</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Užsakymo ID:</label>
            <input
              type="text"
              value={filters.order_id}
              onChange={(e) => handleFilterChange('order_id', e.target.value)}
              className="input"
              placeholder="Ieškoti pagal užsakymo ID..."
            />
          </div>

          <div className="filter-group">
            <label>Vartotojo ID:</label>
            <input
              type="text"
              value={filters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              className="input"
              placeholder="Ieškoti pagal vartotojo ID..."
            />
          </div>

          <div className="filter-group">
            <label>Nuo datos:</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <label>Iki datos:</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({
                  status: '',
                  order_id: '',
                  user_id: '',
                  date_from: '',
                  date_to: '',
                });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      {/* Sąskaitų faktūrų sąrašas */}
      <div className="invoices-table-wrapper">
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Sąskaitos Nr.</th>
              <th>Užsakymo Nr.</th>
              <th>Klientas</th>
              <th>Data</th>
              <th>Terminas</th>
              <th>Suma</th>
              <th>Statusas</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {data?.invoices?.map((invoice) => (
              <tr key={invoice.id}>
                <td>
                  <Link to={`/admin/invoices/${invoice.id}`} className="invoice-link">
                    {invoice.invoice_number}
                  </Link>
                </td>
                <td>
                  <Link to={`/admin/orders/${invoice.order_id}`} className="order-link">
                    {invoice.order_number}
                  </Link>
                </td>
                <td>
                  {invoice.user_email || (
                    <span className="guest-customer">Svečias</span>
                  )}
                  {invoice.first_name && (
                    <div className="customer-name">
                      {invoice.first_name} {invoice.last_name}
                    </div>
                  )}
                </td>
                <td>{formatDate(invoice.issue_date)}</td>
                <td>{formatDate(invoice.due_date)}</td>
                <td className="amount">{parseFloat(invoice.total).toFixed(2)} €</td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(invoice.status)}`}>
                    {getStatusLabel(invoice.status)}
                  </span>
                </td>
                <td>
                  <Link
                    to={`/admin/invoices/${invoice.id}`}
                    className="btn-icon btn-icon-secondary"
                    title="Peržiūrėti"
                  >
                    <ViewIcon />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {data?.pagination && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.pages}
          totalItems={data.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Sukurti sąskaitą faktūrą modalas */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Sukurti sąskaitą faktūrą</h2>
            
            <div className="form-group">
              <label>Užsakymo ID *</label>
              <input
                type="number"
                value={createForm.order_id}
                onChange={(e) => setCreateForm({ ...createForm, order_id: e.target.value })}
                className="input"
                required
                placeholder="Užsakymo ID"
              />
            </div>

            <div className="form-group">
              <label>Išrašymo data *</label>
              <input
                type="date"
                value={createForm.issue_date}
                onChange={(e) => setCreateForm({ ...createForm, issue_date: e.target.value })}
                className="input"
                required
              />
            </div>

            <div className="form-group">
              <label>Mokėjimo terminas</label>
              <input
                type="date"
                value={createForm.due_date}
                onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })}
                className="input"
              />
            </div>

            <div className="form-group">
              <label>Pastabos</label>
              <textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                className="input"
                rows="3"
                placeholder="Papildomos pastabos..."
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Atšaukti
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isLoading}
                className="btn btn-primary"
              >
                {createMutation.isLoading ? 'Kuriama...' : 'Sukurti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInvoices;
